// Offline-First Invitation Service
// Primary storage: IndexedDB, Sync target: Supabase
// Handles organization and team invitations with offline support

import { TeamInvitation } from '../services/teamService';
import { OrganizationInvitation } from '../types/organization';
import { offlineFirstDataManager } from './offlineFirstDataManager';

export interface CachedInvitation {
  id: string;
  type: 'organization' | 'team';
  data: OrganizationInvitation | TeamInvitation;
  created_at: string;
  updated_at: string;
  _offline_modified?: boolean;
  _sync_pending?: boolean;
}

export interface InvitationResult {
  success: boolean;
  organizationInvitations: OrganizationInvitation[];
  teamInvitations: TeamInvitation[];
  error?: string;
  fromCache: boolean;
  needsSync?: boolean;
}

export interface InvitationActionResult {
  success: boolean;
  error?: string;
  needsSync?: boolean;
}

export class OfflineFirstInvitationService {
  private static instance: OfflineFirstInvitationService;
  
  static getInstance(): OfflineFirstInvitationService {
    if (!OfflineFirstInvitationService.instance) {
      OfflineFirstInvitationService.instance = new OfflineFirstInvitationService();
    }
    return OfflineFirstInvitationService.instance;
  }

  /**
   * Get all pending invitations - Always from IndexedDB first (offline-first)
   */
  async getPendingInvitations(): Promise<InvitationResult> {
    try {
      console.log('[OfflineFirstInvitationService] Getting pending invitations (offline-first)');
      
      // Get cached invitations from IndexedDB
      const result = await offlineFirstDataManager.getData<CachedInvitation[]>('_cached_invitations');
      
      if (result.success && result.data) {
        const invitations = Array.isArray(result.data) ? result.data : [result.data];
        
        // Separate by type
        const organizationInvitations: OrganizationInvitation[] = [];
        const teamInvitations: TeamInvitation[] = [];
        
        invitations.forEach(cached => {
          if (cached.type === 'organization') {
            organizationInvitations.push(cached.data as OrganizationInvitation);
          } else if (cached.type === 'team') {
            teamInvitations.push(cached.data as TeamInvitation);
          }
        });

        return {
          success: true,
          organizationInvitations,
          teamInvitations,
          fromCache: true,
          needsSync: result.needsSync
        };
      } else {
        // No cached invitations found
        return {
          success: true,
          organizationInvitations: [],
          teamInvitations: [],
          fromCache: true,
          needsSync: false
        };
      }
    } catch (error) {
      console.error('[OfflineFirstInvitationService] Failed to get invitations:', error);
      return {
        success: false,
        organizationInvitations: [],
        teamInvitations: [],
        error: error instanceof Error ? error.message : 'Failed to get invitations',
        fromCache: true
      };
    }
  }

  /**
   * Accept invitation - Always to IndexedDB first
   */
  async acceptInvitation(
    invitationId: string, 
    type: 'organization' | 'team'
  ): Promise<InvitationActionResult> {
    try {
      console.log('[OfflineFirstInvitationService] Accepting invitation:', invitationId);

      // Mark invitation as accepted in IndexedDB
      const actionData = {
        id: `accept_${invitationId}`,
        invitation_id: invitationId,
        type,
        action: 'accept',
        timestamp: new Date().toISOString()
      };

      // Save action to IndexedDB (will be synced later)
      const saveResult = await offlineFirstDataManager.saveData(
        '_invitation_actions', 
        actionData, 
        'INSERT'
      );

      if (saveResult.success) {
        // Remove invitation from cache
        await this.removeInvitationFromCache(invitationId);

        return {
          success: true,
          needsSync: saveResult.needsSync
        };
      } else {
        return {
          success: false,
          error: saveResult.error || 'Failed to accept invitation'
        };
      }
    } catch (error) {
      console.error('[OfflineFirstInvitationService] Failed to accept invitation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to accept invitation'
      };
    }
  }

  /**
   * Reject invitation - Always to IndexedDB first
   */
  async rejectInvitation(
    invitationId: string, 
    type: 'organization' | 'team'
  ): Promise<InvitationActionResult> {
    try {
      console.log('[OfflineFirstInvitationService] Rejecting invitation:', invitationId);

      // Mark invitation as rejected in IndexedDB
      const actionData = {
        id: `reject_${invitationId}`,
        invitation_id: invitationId,
        type,
        action: 'reject',
        timestamp: new Date().toISOString()
      };

      // Save action to IndexedDB (will be synced later)
      const saveResult = await offlineFirstDataManager.saveData(
        '_invitation_actions', 
        actionData, 
        'INSERT'
      );

      if (saveResult.success) {
        // Remove invitation from cache
        await this.removeInvitationFromCache(invitationId);

        return {
          success: true,
          needsSync: saveResult.needsSync
        };
      } else {
        return {
          success: false,
          error: saveResult.error || 'Failed to reject invitation'
        };
      }
    } catch (error) {
      console.error('[OfflineFirstInvitationService] Failed to reject invitation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reject invitation'
      };
    }
  }

  /**
   * Cache invitations from Supabase (called during sync)
   */
  async cacheInvitations(
    organizationInvitations: OrganizationInvitation[],
    teamInvitations: TeamInvitation[]
  ): Promise<void> {
    try {
      console.log('[OfflineFirstInvitationService] Caching invitations');

      const cachedInvitations: CachedInvitation[] = [
        ...organizationInvitations
          .filter(inv => inv.id) // Validate ID exists
          .map(inv => ({
            id: `org_${inv.id}`,
            type: 'organization' as const,
            data: inv,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })),
        ...teamInvitations
          .filter(inv => inv.id || inv.group_id) // Validate ID or group_id exists (TeamInvitation has group_id, not team_id)
          .map(inv => ({
            id: `team_${inv.id || inv.group_id}`, // Use group_id as fallback
            type: 'team' as const,
            data: inv,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }))
      ];

      // Clear existing cached invitations and store new ones
      await this.clearCachedInvitations();
      
      for (const cached of cachedInvitations) {
        await offlineFirstDataManager.saveData('_cached_invitations', cached, 'UPDATE');
      }

      console.log(`[OfflineFirstInvitationService] Cached ${cachedInvitations.length} invitations`);
    } catch (error) {
      console.error('[OfflineFirstInvitationService] Failed to cache invitations:', error);
    }
  }

  /**
   * Remove invitation from cache
   */
  private async removeInvitationFromCache(invitationId: string): Promise<void> {
    try {
      // Get all cached invitations
      const result = await offlineFirstDataManager.getData<CachedInvitation[]>('_cached_invitations');
      
      if (result.success && result.data) {
        const invitations = Array.isArray(result.data) ? result.data : [result.data];
        
        // Filter out the accepted/rejected invitation
        const filteredInvitations = invitations.filter(cached => {
          if (cached.type === 'organization') {
            return (cached.data as OrganizationInvitation).id !== invitationId;
          } else {
            return (cached.data as TeamInvitation).id !== invitationId;
          }
        });

        // Clear and re-save filtered invitations
        await this.clearCachedInvitations();
        
        for (const cached of filteredInvitations) {
          await offlineFirstDataManager.saveData('_cached_invitations', cached, 'UPDATE');
        }
      }
    } catch (error) {
      console.error('[OfflineFirstInvitationService] Failed to remove invitation from cache:', error);
    }
  }

  /**
   * Clear all cached invitations
   */
  private async clearCachedInvitations(): Promise<void> {
    try {
      // This would need to be implemented in the data manager
      // For now, we'll mark all as deleted
      const result = await offlineFirstDataManager.getData<CachedInvitation[]>('_cached_invitations');
      
      if (result.success && result.data) {
        const invitations = Array.isArray(result.data) ? result.data : [result.data];
        
        for (const cached of invitations) {
          await offlineFirstDataManager.saveData('_cached_invitations', { id: cached.id }, 'DELETE');
        }
      }
    } catch (error) {
      console.error('[OfflineFirstInvitationService] Failed to clear cached invitations:', error);
    }
  }

  /**
   * Get sync status for invitations
   */
  async getInvitationSyncStatus(): Promise<{
    needsSync: boolean;
    pendingActions: number;
  }> {
    try {
      const syncStatus = offlineFirstDataManager.getSyncStatus();
      
      return {
        needsSync: syncStatus.pending > 0,
        pendingActions: syncStatus.pending
      };
    } catch (error) {
      console.error('[OfflineFirstInvitationService] Failed to get sync status:', error);
      return {
        needsSync: false,
        pendingActions: 0
      };
    }
  }

  /**
   * Check if invitations exist in cache
   */
  async hasInvitations(): Promise<boolean> {
    try {
      const result = await this.getPendingInvitations();
      return result.success && 
             (result.organizationInvitations.length > 0 || result.teamInvitations.length > 0);
    } catch (error) {
      console.error('[OfflineFirstInvitationService] Failed to check invitations:', error);
      return false;
    }
  }
}

// Export singleton instance
export const offlineFirstInvitationService = OfflineFirstInvitationService.getInstance();
